import { Suspense, useRef } from "react";
import { usePaginationFragment } from "react-relay";

import { useResourcesListFiltering } from "../../ResourcesList/resourcesList";
import Spinner from "../../Utils/Spinner";

// This fragment is used as the source to refetch contributions data when a filter or the
// sort order are changed in a contributions list. Whenever one of those parameters are
// updated in the redux store, this component is notified through the
// `useResourcesListFiltering` hook's callback, where the data fragment is refetched.
//
// Once the data is refetched, every component that listens to the reloading of the data
// are notified that new data for the fragment was fetched, so they can locally-reload
// their own fragment data from the store.
//
// The fragment and its parent data owner used to refetch the data when it's
// filtered is provided by the parent component through its `refetchFragment` and
// `refetchFragmentParent` props.
//
// The parametric nature of this component makes it usable across all participative
// modules that implement filtering and sorting.
//
// Props :
//
//   - refetchFragment : The fragment to be used for fetching data when filtering or
//                       sorting is applied. It must be a refetchable fragment that has
//                       a `contributions` field with a @connection, and must use the
//                       $contributionsFilters and optionally the $contributionsSort
//                       variables as arguments to its @connection directive.
//
//   - refetchFragmentParent : The fragment data owner that's used to fetch the fragment
//                             from. This is a Relay javascript object which results from
//                             a previous `useFragment` call, which fragment must have the
//                             the provided `refetchFragment`'s fragment spread inside.
//                             This is often the parent component's `participativeModule`
//                             relay-backed variable.
//
//   - spinnerClassName : The className of the fallback <Spinner> component used while the
//                        data is being fetched.
//
// Example :
//
//    const participativeModule = useFragment(
//      graphql[
//        fragment Component_participativeModule on ParticipativeModule {
//          ...DataFragment_participativeModule
//        }
//      ], props.participativeModule
//    );
//
//    return (
//      <ContributionsListFiltersLoader
//        refetchFragment={DataFragment_participativeModule}
//        refetchFragmentOwner={participativeModule}
//      />
//    );
//
//
const ContributionsListFiltersLoaderInner = props => {
  const { refetchFragment, refetchFragmentParent } = props;
  const { refetch } = usePaginationFragment(refetchFragment, refetchFragmentParent);
  const refetchRequestRef = useRef();

  useResourcesListFiltering(
    ({ filters: contributionsFilters, sort: contributionsSort, onComplete }) => {
      // Cancels a potential current refetch request.
      if (refetchRequestRef.current) {
        refetchRequestRef.current.dispose();
      }

      // Refetches the fragment with the updated filters and sort variables and registers
      // the current refetch request to be able to dispose it if another refetch is called
      // before this one has complete.
      refetchRequestRef.current = refetch(
        { contributionsFilters, contributionsSort },
        {
          onComplete: (...args) => {
            refetchRequestRef.current = null;
            onComplete(...args);
          }
        }
      );
    }
  );

  return <>{props.children}</>;
};

// This is a suspendable wrapper that allows for the actual component to be suspended
// without suspending the whole parent hierarchy until the next Suspense wrapper, and
// just replace the component's children with a loader.
//
const ContributionsListFiltersLoader = props => {
  const { spinnerClassName, ...childProps } = props;

  return (
    <Suspense fallback={<Spinner className={spinnerClassName} />}>
      <ContributionsListFiltersLoaderInner {...childProps} />
    </Suspense>
  );
};

export default ContributionsListFiltersLoader;
